import { UIIcon } from '../../../config/icons';
import { FPageBlock, FPageButton, FPageButtonColor } from '../../mdl/f-page-block';
import { UIInputAdapter } from '../../ui/component/adapter';
import { UIButtonKind } from '../../ui/component/button';
import { UITextInput } from '../../ui/component/input';
import { UILabeled } from '../../ui/component/labeled';
import { UIList } from '../../ui/component/list';
import { UISelect } from '../../ui/component/select';
import { UICheckbox } from '../../ui/component/toggle';
import { $el } from '../../ui/elements';
import { UIValue, UIPoint } from '../../ui/types';
import { UIView, UIViewSpec } from '../../ui/view';
import { $on } from '../../y/events';
import { YForm, $form } from '../../y/form';
import { EDBlock, EDBlockSpec } from '../block';
import { EDObject, EDBlockContainer, EDButtonSpec } from '../editor';
import { EDI18NInput } from '../input';
import { $page_values } from './values';

const button_color_values: UIValue<FPageButtonColor>[] = [{ key: 'tint', name: 'Tint' }, { key: 'fg', name: 'Foreground' }, { key: 'bg', name: 'Background' }];
const button_kind_values: UIValue<UIButtonKind>[] = [{ key: 'k-default', name: 'Frame' }, { key: 'k-text', name: 'Text' }];
const icon_values: UIValue<UIIcon | undefined>[] = [{ key: undefined, name: 'None' }, { key: UIIcon.BRAND_APPLE, name: 'Apple' }, { key: UIIcon.BRAND_GOOGLE_PLAY, name: 'Google Play' }];

export class EditorButton extends UIView implements EDObject {
    properties: UIViewSpec;
    parent: EDBlock;
    form: YForm;
    name = 'Button';
    doRemove?: () => void;

    constructor(block: EDBlock) {
        let left_icon: HTMLElement = $el.DIV();
        const text = new EDI18NInput({ placeholder: 'Text', fields: block.editor.fields, classes: 'r-text' });

        super('div', ['ed-object', 'ui-button', 'ui-tint-black', 'k-shadow'], [left_icon, text]);
        this.ui_elem.ed_object = this;
        this.parent = block;

        this.root.addEventListener('click', e => {
            block.editor.selectObject(this);
            e.stopPropagation();
        });

        const link = new UITextInput({ kind: 'k-subtle' });
        const icon = new UISelect(icon_values, { kind: 'k-subtle' });
        const kind = new UISelect(button_kind_values, { kind: 'k-subtle' });
        const color = new UISelect(button_color_values, { kind: 'k-subtle' });
        const hollow = new UICheckbox({ title: 'Hollow' });
        const larger = new UICheckbox({ title: 'Larger' });

        this.form = $form({ text, link, icon, kind, color, hollow, larger })

        this.properties = $el.DIV('l-form', [
            new UILabeled(link, { above: 'Link' }),
            new UILabeled(icon, { above: 'Icon' }),
            new UILabeled(kind, { above: 'Kind' }),
            new UILabeled(color, { above: 'Color' }),
            hollow,
            larger
        ]);

        $on(icon, 'value', v => {
            if(v) {
                const new_icon = $el.ICON(v, 'r-left');
                left_icon.ui_replace(new_icon);
                left_icon = new_icon;
            } else {
                const new_icon = $el.DIV();
                left_icon.ui_replace(new_icon);
                left_icon = new_icon;
            }
        });

        $on(color, 'value', v => {
            this.classList.toggle('ui-tint-reverse', v == 'fg');
            this.classList.toggle('ui-tint-bg', v == 'bg');
        });

        $on(hollow, 'value', v => {
            this.classList.toggle('m-hollow', !!v);
        });

        $on(larger, 'value', v => {
            this.classList.toggle('m-larger', !!v);
        });

        $on(kind, 'value', v => {
            this.classList.toggle('k-text', v == 'k-text');
            this.classList.toggle('k-default', v == 'k-default');
        });
    }
}

export const ed_page_buttons_block: EDBlockSpec<FPageBlock> = {
    key: 'buttons',
    editor_block: class extends EDBlock {
        private _buttons: UIInputAdapter<FPageButton, EditorButton>;

        constructor(container: EDBlockContainer) {
            const column = new UICheckbox({ title: 'Column' });
            const align = new UISelect($page_values.item_align, { kind: 'k-subtle' });

            const buttons = new UIInputAdapter<FPageButton, EditorButton>((_, entry) => {
                const item: EditorButton = new EditorButton(this)
                item.doRemove = () => { buttons.remove(entry); };
                return { view: item, input: item.form };
            });

            const properties = $el.DIV('l-form', [
                column,
                new UILabeled(align, { above: 'Align' }),
            ]);

            const list = new UIList<UIViewSpec>(buttons, { classes: ['l-button-row', 'w-block-1200', 'm-left', 'b', 'b-content'] });
            super($form({ buttons, column, align }), container, 'Buttons', properties, list.ui_elem);

            this._buttons = buttons;

            $on(column, 'value', v => {
                this.classList.toggle('m-column', !!v);
            });
        }

        override getButtons(): EDButtonSpec[] {
            const rect = this.ui_elem.getBoundingClientRect();
            const views = this._buttons.views;
            const pos: UIPoint = [rect.left + rect.width / 2, rect.top + rect.height / 2];

            if(views.length) {
                const last_item = views[views.length - 1];
                const bounding = last_item.ui_elem.getBoundingClientRect();
                pos[0] = bounding.right + 20;
                pos[1] = bounding.top + bounding.height / 2;
            }

            return [{
                icon: UIIcon.PLUS,
                classes: 'm-round',
                action: () => {
                    this._buttons.push({ color: 'tint', text: {}, link: '', kind: 'k-default', hollow: false, larger: false });
                },
                position: pos,
                tooltip: 'Add button'
            }, ...this.parent.getChildButtons(this)];
        }

        get children() {
            return this._buttons.views;
        }
    },
};
